<template>
  <!-- 신규 상담 등록 HITL 카드(AA-117) — 사용자가 [등록]을 눌러야 실제 커밋된다.
       DraftConfirmBlock(접수 초안)과 대칭이지만 별도 컴포넌트 — 상담 초안은 multi_fields/동적
       확인사항 후보가 없어 구조가 더 단순하다(코드 그룹 4종 + 자유입력 5종 + 고객사 refine 1종). -->
  <div class="aw-draft">
    <div class="aw-draft-title">{{ $t('assistant.consultationDraftTitle') }}</div>
    <!-- 초안 내용은 칩 선택·입력값을 즉시 반영(displayPreview) — DraftConfirmBlock과 동일 원칙 -->
    <dl class="aw-draft-list">
      <template v-for="(val, key) in displayPreview" :key="key">
        <dt>{{ key }}</dt>
        <dd>{{ val }}</dd>
      </template>
    </dl>
    <div v-if="displayMissing.length" class="aw-draft-warn">
      {{ $t('assistant.draftMissing', { fields: displayMissing.join(', ') }) }}
    </div>
    <!-- 카드에서 채울 항목(fieldRows) — 후보 그룹(칩/드롭다운) 다음 직접 입력 순.
         고객사는 code 없는 그룹이라 클릭 즉시 재작성 발화(refine) — 나머지 4개 그룹은 로컬 선택. -->
    <div v-if="!done && fieldRows.length" class="aw-draft-candidates">
      <div class="aw-draft-cand-hint">{{ $t(fieldsHintKey) }}</div>
      <div v-for="row in fieldRows" :key="row.key" class="aw-draft-cand-group">
        <span class="aw-draft-cand-field">{{ row.label }}</span>
        <textarea
          v-if="row.kind === 'input' && row.key === 'content'"
          class="aw-draft-textarea"
          rows="3"
          :aria-label="row.label"
          :placeholder="row.label"
          :disabled="committing"
          :value="inputs[row.key] || ''"
          @input="onInput(row.key, $event.target.value)"
        ></textarea>
        <input
          v-else-if="row.kind === 'input'"
          class="aw-draft-input"
          :type="row.key === 'customer_phone' ? 'tel' : 'text'"
          :aria-label="row.label"
          :placeholder="row.label"
          :disabled="committing"
          :value="inputs[row.key] || ''"
          @input="onInput(row.key, $event.target.value)"
        >
        <select
          v-else-if="isSelectField(row.field)"
          class="aw-draft-cand-select"
          :aria-label="row.field"
          :disabled="committing"
          :value="selected[row.field] !== undefined ? selected[row.field] : ''"
          @change="onSelectChange(row.field, $event.target.value)"
        >
          <option value="">{{ $t('assistant.draftSelectPlaceholder') }}</option>
          <option v-for="(c, i) in row.items" :key="row.key + i" :value="i">{{ c.label }}</option>
        </select>
        <template v-else>
          <button
            v-for="(c, i) in row.items"
            :key="row.key + i"
            type="button"
            class="aw-draft-cand-chip"
            :class="{ 'aw-draft-cand-chip--selected': isSelected(row.field, i) }"
            :disabled="committing"
            @click="onChipClick(row.field, i, c)"
          >
            {{ c.label }}
          </button>
        </template>
      </div>
    </div>
    <div v-if="committing && !done" class="aw-draft-status">{{ $t('assistant.committing') }}</div>
    <div v-if="!done" class="aw-draft-actions">
      <button
        type="button"
        class="aw-draft-btn aw-draft-commit"
        :disabled="registerDisabled"
        @click="$emit('commit', commitPayload)"
      >
        {{ $t('assistant.register') }}
      </button>
      <button
        type="button"
        class="aw-draft-btn aw-draft-cancel"
        :disabled="committing"
        @click="$emit('cancel')"
      >
        {{ $t('assistant.cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
/** 후보 그룹명 → commit 페이로드 키 — ai-service CommitRequest 계약과 고정 동기화 (§4.3) */
const GROUP_COMMIT_KEYS = { 제품군: 'product_model_id', 문의유형: 'main_type_code', 유입경로: 'funnel_code', 사용자그룹: 'user_group_id' }

/** 후보 그룹명 → preview 행 라벨 — ai-service prepare_consultation_draft의 preview 키와 동기화
    (사용자그룹만 preview 키에 공백이 있다 — csback.py user_group_label 계약) */
const GROUP_PREVIEW_KEYS = { 제품군: '제품군', 문의유형: '문의유형', 유입경로: '유입경로', 사용자그룹: '사용자 그룹' }

/** 직접 입력 필드 key → preview 행 라벨 — 전화번호만 preview에서 '전화'로 축약 표기.
    email 은 ai-service csback._CONSULT_EMAIL_LABEL(preview 행 키)과 같은 문자열 — 기존 고객이면 저장되지 않는 조건을 행 이름에 싣는다(AA-144) */
const INPUT_PREVIEW_KEYS = { customer_name: '고객명', customer_phone: '전화', title: '제목', content: '내용', memo: '메모', business_registration_number: '사업자번호', email: '이메일(신규 고객만 저장)' }

/**
 * @component ConsultationDraftBlock
 * @description HITL 상담 등록 확인 카드(AA-117). data={preview, missing, draft_id, confirmation_token,
 *   candidates?, input_fields?, select_fields?}. DraftConfirmBlock(접수 초안)과 대칭 설계지만
 *   상담 초안은 multi_fields/동적 확인사항이 없어 더 단순하다.
 *   - 코드(code) 있는 그룹(제품군/문의유형/유입경로/사용자그룹): 로컬 단일 선택(칩 또는
 *     select_fields 지정 시 드롭다운 — 유입경로는 전량 후보라 드롭다운). 코드 없는 그룹(고객사):
 *     클릭 즉시 재작성 발화(query) 전송 — 접수 초안의 '지역' 처리와 동일 패턴.
 *   - 직접 입력(input_fields — 고객명/전화번호/제목/상담내용/메모): 미입력 슬롯만 카드에 노출.
 *     이 중 필수(고객명/전화번호/상담내용)는 data.missing에 대응 항목이 있을 때만 [등록] 게이트에
 *     포함한다 — 제목/메모는 선택 입력이라 비워도 등록 가능(§4.1 계약: content만 화면보다 엄격).
 *   - [등록]: 카드에서 채울 수 없는 missing(예: 고객사 미확정) 없음 + 코드 그룹 전부 선택 +
 *     필수 직접 입력 전부 기입 시 활성화. 클릭 시 선택값+입력값을 commit 페이로드로 emit —
 *     BE가 draft.allowed_selections 화이트리스트로 재검증한다.
 *   - 초안 내용 동기 표시: 칩 선택·입력 즉시 preview 행에 반영, 충족된 missing 경고는 감춘다.
 */
export default {
  name: 'ConsultationDraftBlock',
  props: {
    data: { type: Object, default: () => ({}) },
    committing: { type: Boolean, default: false },
    done: { type: Boolean, default: false }
  },
  emits: ['commit', 'cancel', 'refine'],
  data() {
    return {
      selected: {}, // {필드: 칩/드롭다운 index}
      inputs: {} // {input_fields key: 입력 텍스트}
    }
  },
  computed: {
    displayCandidates() {
      return this.data.candidates || {}
    },
    hasCandidates() {
      return Object.keys(this.displayCandidates).length > 0
    },
    /** 카드 내 직접 입력 필드(고객명/전화번호/제목/상담내용/메모) — prepare가 미입력분만 내려준다 */
    inputFields() {
      const list = Array.isArray(this.data.input_fields) ? this.data.input_fields : []
      return list.filter(f => f && f.key && f.label)
    },
    /** 필수 직접 입력 — data.missing에 대응 항목이 있는 필드만([등록] 게이트 대상). 제목/메모는
        일반적으로 missing에 없어 선택 입력으로 취급된다. */
    requiredInputFields() {
      const norm = s => String(s).replace(/\s+/g, '')
      const missing = this.data.missing || []
      return this.inputFields.filter(f => missing.some(m => norm(m).startsWith(norm(f.label))))
    },
    /** 카드에서 채울 항목의 렌더 순서 — 후보 그룹(칩/드롭다운) 다음 직접 입력(선택 입력 포함) */
    fieldRows() {
      const rows = []
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        rows.push({ kind: 'group', key: field, field, label: field, items })
      }
      for (const f of this.inputFields) {
        rows.push({ kind: 'input', key: f.key, label: f.label })
      }
      return rows
    },
    fieldsHintKey() {
      return this.hasCandidates ? 'assistant.candidateHint' : 'assistant.draftInputHint'
    },
    /** 값이 입력된 직접 입력 필드 — {commit 페이로드 key: trim 값} (선택 입력도 채워졌으면 포함) */
    filledInputs() {
      const out = {}
      for (const f of this.inputFields) {
        const v = String(this.inputs[f.key] || '').trim()
        if (v) out[f.key] = v
      }
      return out
    },
    /** 코드 그룹별 선택 라벨 — {정규화 그룹명: 선택 라벨} */
    selectedLabels() {
      const norm = s => String(s).replace(/\s+/g, '')
      const out = {}
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        if (!this.groupHasCodes(field)) continue
        const i = this.selected[field]
        if (i !== undefined && items[i]) out[norm(field)] = items[i].label
      }
      return out
    },
    /** 초안 표시 내용 — preview 위에 칩 선택 라벨·직접 입력값을 겹쳐 실제 등록될 값과 일치시킨다 */
    displayPreview() {
      const out = { ...(this.data.preview || {}) }
      for (const [group, label] of Object.entries(this.selectedLabels)) {
        const key = GROUP_PREVIEW_KEYS[group]
        if (key) out[key] = label
      }
      for (const f of this.inputFields) {
        const v = this.filledInputs[f.key]
        const key = INPUT_PREVIEW_KEYS[f.key]
        if (v && key) out[key] = v
      }
      return out
    },
    /** missing 경고 표시분 — 칩 선택·직접 입력으로 이미 충족된 항목은 제외 */
    displayMissing() {
      const norm = s => String(s).replace(/\s+/g, '')
      const satisfied = Object.keys(this.selectedLabels)
      for (const f of this.inputFields) {
        if (this.filledInputs[f.key]) satisfied.push(norm(f.label))
      }
      return (this.data.missing || []).filter(m => !satisfied.some(f => norm(m).startsWith(f)))
    },
    /** commit 페이로드 — 선택된 코드 그룹 값 + 직접 입력값(선택 입력 포함) */
    commitPayload() {
      const out = {}
      for (const [field, items] of Object.entries(this.displayCandidates)) {
        if (!this.groupHasCodes(field)) continue
        const i = this.selected[field]
        if (i === undefined) continue
        const key = GROUP_COMMIT_KEYS[String(field).replace(/\s+/g, '')]
        const code = this.chipCode(items[i])
        if (key && code) out[key] = code
      }
      Object.assign(out, this.filledInputs)
      return out
    },
    /** [등록] 비활성 — 카드에서 채울 수 없는 missing 존재, 코드 그룹 미선택, 또는 필수 입력 미기입 */
    registerDisabled() {
      if (this.committing) return true
      const norm = s => String(s).replace(/\s+/g, '')
      const groups = this.displayCandidates
      const selectable = Object.keys(groups).filter(f => this.groupHasCodes(f)).map(norm)
      for (const f of this.requiredInputFields) selectable.push(norm(f.label))
      const others = (this.data.missing || []).filter(m => !selectable.some(f => norm(m).startsWith(f)))
      if (others.length > 0) return true
      for (const f of this.requiredInputFields) {
        if (!this.filledInputs[f.key]) return true
      }
      for (const [field, items] of Object.entries(groups)) {
        if (!this.groupHasCodes(field)) continue
        if (this.selected[field] === undefined || !items[this.selected[field]]) return true
      }
      return false
    }
  },
  methods: {
    chipCode(c) {
      return (c && c.code) ? String(c.code) : ''
    },
    groupHasCodes(field) {
      const items = this.displayCandidates[field] || []
      return items.some(c => !!this.chipCode(c))
    },
    /** select_fields 계약 그룹(유입경로) — 전량 후보라 칩 대신 드롭다운 렌더 */
    isSelectField(field) {
      const sf = this.data.select_fields
      return Array.isArray(sf) && sf.includes(field)
    },
    onSelectChange(field, val) {
      if (val === '') delete this.selected[field]
      else this.selected[field] = Number(val)
    },
    onInput(key, val) {
      this.inputs[key] = val
    },
    isSelected(field, i) {
      return this.selected[field] === i
    },
    onChipClick(field, i, c) {
      if (!this.groupHasCodes(field)) {
        this.$emit('refine', c.query) // 코드 없는 그룹(고객사) — 즉시 재작성 경로
        return
      }
      const prev = this.selected[field]
      if (prev === i) delete this.selected[field] // 재클릭 = 해제
      else this.selected[field] = i
    }
  }
}
</script>

<style scoped>
.aw-draft {
  max-width: 100%;
  background: var(--color-primary-50, #E8EDF4);
  border: 1px solid var(--color-primary-100, #C5D1E3);
  border-radius: 12px;
  padding: 12px;
  white-space: normal;
}
.aw-draft-title { font-weight: 700; font-size: 14px; color: var(--color-primary-500, #3B5998); margin-bottom: 8px; }
.aw-draft-list { display: grid; grid-template-columns: auto 1fr; gap: 4px 10px; margin: 0 0 8px; font-size: 13px; }
.aw-draft-list dt { font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-list dd { margin: 0; color: var(--color-gray-800, #202124); word-break: break-word; }
.aw-draft-warn {
  background: var(--color-warning-light, #F5EDDA);
  color: var(--color-warning, #C8870A);
  border: 1px solid var(--color-warning, #C8870A);
  border-radius: 8px; padding: 6px 8px; font-size: 12.5px; margin-bottom: 8px;
}
.aw-draft-status { font-size: 12.5px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
.aw-draft-candidates { margin-bottom: 8px; }
.aw-draft-cand-hint { font-size: 12px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
.aw-draft-cand-group { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
.aw-draft-cand-field { font-size: 12px; font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-cand-chip {
  border: 1px solid var(--color-primary-500, #3B5998);
  background: var(--color-white, #fff);
  color: var(--color-primary-600, #2C4578);
  border-radius: var(--radius-full, 9999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.aw-draft-cand-chip:hover:not(:disabled) { background: var(--color-primary-50, #E8EDF4); }
.aw-draft-cand-chip:disabled { border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6); cursor: not-allowed; }
.aw-draft-cand-chip--selected { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-cand-chip--selected:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
.aw-draft-cand-select, .aw-draft-input, .aw-draft-textarea {
  flex: 1 1 160px; min-width: 0; border: 1px solid var(--color-gray-300, #b0bec5);
  background: var(--color-white, #fff); color: var(--color-gray-800, #202124);
  border-radius: var(--radius-md, 8px); padding: 5px 8px; font-size: 12.5px;
}
.aw-draft-textarea { flex-basis: 100%; resize: vertical; font-family: inherit; }
.aw-draft-cand-select { border-color: var(--color-primary-500, #3B5998); }
.aw-draft-cand-select:focus, .aw-draft-input:focus, .aw-draft-textarea:focus { outline: none; border-color: var(--color-primary-500, #3B5998); }
.aw-draft-cand-select:disabled, .aw-draft-input:disabled, .aw-draft-textarea:disabled {
  background: var(--color-gray-100, #eef0f3); border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6);
}
.aw-draft-actions { display: flex; gap: 8px; }
.aw-draft-btn { border: none; border-radius: var(--radius-md, 8px); padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.aw-draft-commit { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-commit:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
.aw-draft-cancel { background: var(--color-gray-100, #eef0f3); color: var(--color-gray-800, #202124); }
.aw-draft-cancel:hover:not(:disabled) { background: var(--color-gray-200, #e0e0e0); }
.aw-draft-btn:disabled { background: var(--color-gray-300, #b0bec5); color: var(--color-white, #fff); cursor: not-allowed; }
</style>
